import { useState, useEffect, useCallback } from 'react';

// Hook for PWA installation prompt
export const useInstallPrompt = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', () => setIsInstalled(true));

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!installPrompt) return false;
    
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    setInstallPrompt(null);
    
    return result.outcome === 'accepted';
  }, [installPrompt]);

  return {
    canInstall: !!installPrompt,
    isInstalled,
    isIOS,
    promptInstall
  };
};

// Hook for push notifications
export const usePushNotifications = () => {
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.log('Notifications not supported');
      return false;
    }

    setLoading(true);
    const result = await Notification.requestPermission();
    setPermission(result);

    if (result === 'granted') {
      // Subscribe to push notifications
      try {
        const registration = await navigator.serviceWorker.ready;
        
        // Get VAPID key from environment or backend
        let vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
        
        if (!vapidKey) {
          // Fallback: fetch from backend
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/push/vapid-key`);
          if (response.ok) {
            const data = await response.json();
            vapidKey = data.publicKey;
          }
        }
        
        if (!vapidKey) {
          console.error('VAPID key not available');
          setLoading(false);
          return false;
        }

        const sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey)
        });
        
        // Send subscription to backend
        const token = localStorage.getItem('token');
        if (token) {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/push/subscribe`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              endpoint: sub.endpoint,
              keys: {
                p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(sub.getKey('p256dh')))),
                auth: btoa(String.fromCharCode.apply(null, new Uint8Array(sub.getKey('auth'))))
              }
            })
          });
        }
        
        setSubscription(sub);
        setLoading(false);
        return true;
      } catch (error) {
        console.error('Push subscription failed:', error);
        setLoading(false);
        return false;
      }
    }

    setLoading(false);
    return false;
  }, []);

  const testNotification = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/push/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.ok;
    } catch (error) {
      console.error('Test notification failed:', error);
      return false;
    }
  }, []);

  const showLocalNotification = useCallback(async (title, options = {}) => {
    if (permission !== 'granted') return;

    const registration = await navigator.serviceWorker.ready;
    registration.showNotification(title, {
      icon: '/icons/icon-192x192.png',
      badge: '/icons/icon-72x72.png',
      vibrate: [100, 50, 100],
      ...options
    });
  }, [permission]);

  return {
    permission,
    subscription,
    loading,
    requestPermission,
    testNotification,
    showLocalNotification,
    isSupported: 'Notification' in window && 'serviceWorker' in navigator
  };
};

// Hook for geolocation
export const useGeolocation = () => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Géolocalisation non supportée');
      return;
    }

    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes cache
      }
    );
  }, []);

  return {
    location,
    error,
    loading,
    getCurrentLocation,
    isSupported: 'geolocation' in navigator
  };
};

// Hook for online/offline status
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

// Helper function for VAPID key conversion
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default {
  useInstallPrompt,
  usePushNotifications,
  useGeolocation,
  useOnlineStatus
};
